import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle } from "@headlessui/react";
import tw from "twin.macro";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Pagination from "components/misc/Pagination";
import Footer from "components/footers/FourColumnWithInputForm.js";
import Header from "components/headers/HeaderLight";
import { SectionHeading } from "components/misc/Headings.js";
import { useAnnouncements } from "hooks/useAnnouncements";
import FilterByDates from "components/misc/FilterByDates";
import moment from "moment";

const Container = tw.div`relative`;
const Table = tw.table`min-w-full bg-white rounded-lg shadow-md`;
const Th = tw.th`py-3 px-4 bg-primary-500 text-white font-semibold text-left`;
const Td = tw.td`py-2 px-4 border-b border-gray-200 text-gray-700`;
const Tr = tw.tr`hover:bg-gray-100 transition-colors`;
const Heading = tw(
  SectionHeading
)`mt-4 font-black text-3xl sm:text-4xl lg:text-5xl text-center leading-tight`;
const Button = tw.button`ml-2 px-3 py-1 rounded bg-primary-500 text-white font-semibold hover:bg-primary-700 transition`;

const initialForm = {
  title: "",
  description: "",
  category: "",
  date: "",
  imageSrc: "",
  featured: false
};

const AdminAnnouncementsPage = () => {
  const { announcements, loading, fetchAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement } = useAnnouncements();
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedDateStart, setSelectedDateStart] = useState(null);
  const [selectedDateEnd, setSelectedDateEnd] = useState(null);

  useEffect(() => { fetchAnnouncements(); }, []);

  const openCreateModal = () => {
    setForm(initialForm);
    setEditMode(false);
    setModalOpen(true);
    setSelectedAnnouncement(null);
  };
  const openEditModal = (a) => {
    setForm({ ...a });
    setEditMode(true);
    setModalOpen(true);
    setSelectedAnnouncement(a);
  };
  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => {
      setForm(initialForm);
      setSelectedAnnouncement(null);
    }, 200);
  };
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editMode) {
      await updateAnnouncement({ ...selectedAnnouncement, ...form });
    } else {
      await createAnnouncement(form);
    }
    closeModal();
  };
  const handleDelete = async (id) => {
    if (window.confirm("¿Seguro que deseas eliminar este anuncio?")) {
      await deleteAnnouncement(id);
    }
  };

  // Pagination state for announcements table
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  let filtered = announcements;
  if (categoryFilter !== "all") filtered = filtered.filter(a => a.category === categoryFilter);
  if (search) {
    const s = search.toLowerCase();
    filtered = filtered.filter(a =>
      a.title.toLowerCase().includes(s) ||
      a.description.toLowerCase().includes(s) ||
      a.category.toLowerCase().includes(s)
    );
  }
  // Date range filter
  if (selectedDateStart || selectedDateEnd) {
    filtered = filtered.filter(a => {
      if (!a.date) return false;
      const date = moment(a.date, [moment.ISO_8601, 'YYYY-MM-DD']);
      let afterStart = true, beforeEnd = true;
      if (selectedDateStart) afterStart = date.isSameOrAfter(selectedDateStart, 'day');
      if (selectedDateEnd) beforeEnd = date.isSameOrBefore(selectedDateEnd, 'day');
      return afterStart && beforeEnd;
    });
  }

  // Reset to first page when filters change
  useEffect(() => { setCurrentPage(1); }, [search, categoryFilter, selectedDateStart, selectedDateEnd, announcements]);

  // Get unique categories for filter dropdown
  const categories = Array.from(new Set(announcements.map(a => a.category))).filter(Boolean);

  return (
    <AnimationRevealPage>
      <Header />
      <Container className="py-12">
        <Heading>Gestión de Anuncios</Heading>
        <div className="flex flex-wrap gap-4 mt-4 justify-center">
          <input
            type="text"
            placeholder="Buscar anuncio..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="px-4 py-2 border rounded shadow-sm"
          />
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="px-4 py-2 border rounded shadow-sm">
            <option value="all">Todas las categorías</option>
            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          <Button onClick={openCreateModal}>Nuevo Anuncio</Button>
        </div>
        {/* Date filter */}
        <div className="flex flex-wrap gap-4 mb-2 justify-center">
          <FilterByDates
            selectedDateStart={selectedDateStart}
            setSelectedDateStart={setSelectedDateStart}
            selectedDateEnd={selectedDateEnd}
            setSelectedDateEnd={setSelectedDateEnd}
          />
        </div>
        {/* Modal for add/edit */}
        {modalOpen && (
          <Dialog open={modalOpen} onClose={closeModal} as="div" className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-25" aria-hidden="true" />
            <div className="bg-white rounded-lg p-8 shadow-lg w-full max-w-md z-50 relative">
              <DialogTitle className="text-xl font-bold mb-4">{editMode ? "Editar Anuncio" : "Nuevo Anuncio"}</DialogTitle>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="title" className="block mb-1 font-semibold">Título</label>
                  <input id="title" name="title" value={form.title} onChange={handleChange} required className="w-full px-3 py-2 border rounded" />
                </div>
                <div className="mb-4">
                  <label htmlFor="description" className="block mb-1 font-semibold">Descripción</label>
                  <textarea id="description" name="description" value={form.description} onChange={handleChange} required className="w-full px-3 py-2 border rounded" rows={4} />
                </div>
                <div className="mb-4">
                  <label htmlFor="category" className="block mb-1 font-semibold">Categoría</label>
                  <input id="category" name="category" value={form.category} onChange={handleChange} required className="w-full px-3 py-2 border rounded" />
                </div>
                <div className="mb-4">
                  <label htmlFor="date" className="block mb-1 font-semibold">Fecha</label>
                  <input id="date" name="date" type="date" value={form.date} onChange={handleChange} required className="w-full px-3 py-2 border rounded" />
                </div>
                <div className="mb-4">
                  <label htmlFor="imageSrc" className="block mb-1 font-semibold">Imagen (URL)</label>
                  <input id="imageSrc" name="imageSrc" value={form.imageSrc} onChange={handleChange} className="w-full px-3 py-2 border rounded" />
                </div>
                <div className="mb-6 flex items-center">
                  <input id="featured" name="featured" type="checkbox" checked={form.featured} onChange={handleChange} className="mr-2" />
                  <label htmlFor="featured" className="font-semibold">Destacado</label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" onClick={closeModal} className="bg-gray-400 hover:bg-gray-600">Cancelar</Button>
                  <Button type="submit">{editMode ? "Guardar Cambios" : "Agregar"}</Button>
                </div>
              </form>
            </div>
          </Dialog>
        )}
        <div className="overflow-x-auto bg-white/90 backdrop-blur-md border border-blue-300 shadow-2xl rounded-3xl">
          <Table>
            <thead>
              <tr>
                <Th>ID</Th>
                <Th>Título</Th>
                <Th>Categoría</Th>
                <Th>Fecha</Th>
                <Th>Destacado</Th>
                <Th>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let rows = [];
                if (loading) {
                  rows.push(<tr key="loading"><Td colSpan={6}>Cargando...</Td></tr>);
                } else if (filtered.length === 0) {
                  rows.push(<tr key="no-data"><Td colSpan={6}>No hay anuncios encontrados.</Td></tr>);
                } else {
                  // Pagination logic
                  const total = filtered.length;
                  const totalPages = Math.ceil(total / pageSize);
                  const startIdx = (currentPage - 1) * pageSize;
                  const endIdx = startIdx + pageSize;
                  const pageData = filtered.slice(startIdx, endIdx);
                  rows = [
                    ...pageData.map(a => (
                      <Tr key={a.id}>
                        <Td>{a.id}</Td>
                        <Td>{a.title}</Td>
                        <Td>{a.category}</Td>
                        <Td>{moment(a.date).format('YYYY-MM-DD')}</Td>
                        <Td>{a.featured ? "Sí" : "No"}</Td>
                        <Td>
                          <Button onClick={() => openEditModal(a)}>Editar</Button>
                          <Button onClick={() => handleDelete(a.id)} className="bg-red-500 hover:bg-red-700 ml-2">Eliminar</Button>
                        </Td>
                      </Tr>
                    ))
                  ];
                  // Pagination controls row
                  rows.push(
                    <tr key="pagination-controls">
                      <Td colSpan={6}>
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={setCurrentPage}
                        />
                      </Td>
                    </tr>
                  );
                }
                return rows;
              })()}
            </tbody>
          </Table>
        </div>
      </Container>
      <Footer />
    </AnimationRevealPage>
  );
};

export default AdminAnnouncementsPage;
